import * as THREE from 'three';
import { mat, mesh, sphere } from './materials';

const LIGHTS = 14;
const COLORS = ['#ff3b3b', '#ffd400', '#39ff5a', '#3ad6ff', '#c77dff'];

/**
 * A flying saucer: a fat silver lens with a glowing dome, chasing rim lights and a
 * tractor beam it can shine straight down. It lives in the sky, hidden, until it's
 * time to come for somebody.
 */
export class Ufo {
  readonly group = new THREE.Group();
  private readonly hull = new THREE.Group();
  private readonly lights: THREE.MeshStandardMaterial[] = [];
  private readonly beam: THREE.Mesh;
  private readonly beamMat: THREE.MeshBasicMaterial;
  private readonly glow: THREE.MeshStandardMaterial;
  /** 0..1 how much beam there is. */
  beamOn = 0;

  constructor(parent: THREE.Object3D) {
    // Silver, with a faint glow so it reads against a night sky.
    const metal = mat('#d8dee6', { metal: 0.45, rough: 0.3, emissive: '#4a5560', emissiveIntensity: 0.5 });
    const lens = sphere(4, metal, 0, 0, 0, 28);
    lens.scale.set(1, 0.2, 1);
    this.hull.add(lens);
    const rim = new THREE.Mesh(
      new THREE.TorusGeometry(3.95, 0.14, 8, 40),
      mat('#8a9098', { metal: 0.5, rough: 0.3, emissive: '#2a3038', emissiveIntensity: 0.5 }),
    );
    rim.rotation.x = Math.PI / 2;
    this.hull.add(rim);
    const dome = mesh(
      new THREE.SphereGeometry(1.6, 20, 10, 0, Math.PI * 2, 0, Math.PI / 2),
      new THREE.MeshStandardMaterial({
        color: '#9ff3e0',
        emissive: new THREE.Color('#2affc0'),
        emissiveIntensity: 0.9,
        transparent: true,
        opacity: 0.75,
        roughness: 0.1,
      }),
      0,
      0.55,
      0,
    );
    this.hull.add(dome);
    for (let i = 0; i < LIGHTS; i++) {
      const a = (i / LIGHTS) * Math.PI * 2;
      const m = new THREE.MeshStandardMaterial({
        color: '#111',
        emissive: new THREE.Color(COLORS[i % COLORS.length]),
        emissiveIntensity: 2,
      });
      this.lights.push(m);
      this.hull.add(
        mesh(new THREE.SphereGeometry(0.18, 8, 6), m, Math.cos(a) * 3.75, -0.12, Math.sin(a) * 3.75, {
          cast: false,
        }),
      );
    }
    this.glow = new THREE.MeshStandardMaterial({
      color: '#c8ffe8',
      emissive: new THREE.Color('#5affb0'),
      emissiveIntensity: 1.6,
    });
    const belly = mesh(new THREE.CircleGeometry(1.4, 24), this.glow, 0, -0.78, 0, { cast: false });
    belly.rotation.x = Math.PI / 2;
    this.hull.add(belly);
    this.group.add(this.hull);
    // The beam: a cone hanging under the saucer, its tip at the belly.
    this.beamMat = new THREE.MeshBasicMaterial({
      color: '#b8ffd8',
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
      fog: false,
    });
    const geo = new THREE.CylinderGeometry(1.2, 3.6, 1, 28, 1, true);
    geo.translate(0, -0.5, 0);
    this.beam = new THREE.Mesh(geo, this.beamMat);
    this.beam.position.y = -0.7;
    this.beam.visible = false;
    this.group.add(this.beam);
    this.group.traverse((o) => {
      o.userData.noBatch = true;
      o.castShadow = false;
    });
    this.group.visible = false;
    parent.add(this.group);
  }

  /** Puts the saucer somewhere (or hides it with null). */
  show(at: THREE.Vector3 | null): void {
    this.group.visible = at !== null;
    if (at) this.group.position.copy(at);
  }

  update(time: number): void {
    if (!this.group.visible) return;
    this.hull.rotation.y = time * 0.6;
    this.hull.position.y = Math.sin(time * 1.3) * 0.25;
    this.hull.rotation.z = Math.sin(time * 0.9) * 0.04;
    const lead = Math.floor(time * 12) % LIGHTS;
    this.lights.forEach((m, i) => (m.emissiveIntensity = i === lead || (i + 7) % LIGHTS === lead ? 4 : 1.2));
    this.glow.emissiveIntensity = 1.4 + Math.sin(time * 4) * 0.4;
    // The beam reaches the ground from wherever the saucer hangs.
    const h = this.group.position.y - 0.7;
    this.beam.visible = this.beamOn > 0.01 && h > 0.5;
    this.beam.scale.set(1, h, 1);
    this.beamMat.opacity = this.beamOn * (0.2 + Math.sin(time * 9) * 0.04);
  }
}
